<!--
 * @Author: yang.yang
 * @Description: 顶部header
 -->

<template>
  <div
    ref="header"
    class="header-wrap"
    :class="{
      'sticky': sticky,
    }"
    :style="{
      background: themedBackground,
      paddingTop: safePaddingTop,
    }"
  >
    <div
      class="header-self"
      :class="{
        'fixed': fixed,
      }"
      :style="{
        color: themedColor,
        background: themedBackground,
        paddingTop: fixed ? safePaddingTop : 0,
        borderBottom: borderColor === 'none' ? 'none' : `1PX solid ${ themedBorderColor }`,
      }"
    >
      <div class="content" :class="{'new':isNewVersion}" v-if="isNewVersion">
        <div class="btn-back" @click="routeBack">
          <div class="icon-box">
            <slot name="back-icon">
              <component v-if="backComponent" :is="backComponent" :class="{ 'hidden': hideBack }" />
              <component v-else :is="backIconComponent" :class="{ 'hidden': hideBack }" />
            </slot>
          </div>
          <slot name="message"></slot>
        </div>
        <div class="title">
          <slot name="title">
            <span>
              {{ title }}
            </span>
            <span class="small-title" v-if="smallTitle">
              {{ smallTitle }}
            </span>
          </slot>
        </div>
        <div class="tools">
          <slot name="tools"></slot>
        </div>
      </div>
      <div class="content" v-else>
        <div class="btn-back" @click="routeBack">
          <img v-if="backSrc" :class="{ 'hidden': hideBack }" :src="backSrc" />
          <slot name="message"></slot>
        </div>
        <div class="title">
          <slot name="title">
            {{ title }}
          </slot>
        </div>
        <div class="tools">
          <slot name="tools"></slot>
        </div>
      </div>
    </div>
    <div v-if="fixed" class="header-hold"></div>
  </div>
</template>

<script>
import BackIcon from '@/icons/back.svg';
import BackDarkIcon from '@/icons/back-dark.svg';
import { isPageDark } from '@/script/theme';

// 深色頁面裡，把頁面傳進來的淺色底 / 深色字換成主題色（只在已適配深色的頁面生效）
function parseHexColor(value) {
  const hex = String(value || '').trim().replace(/^#/, '');
  if (!/^([0-9a-f]{3}|[0-9a-f]{6})$/i.test(hex)) return null;
  const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
  return [0, 2, 4].map(i => parseInt(full.slice(i, i + 2), 16));
}

function luminance(value) {
  const named = { white: [255, 255, 255], black: [0, 0, 0] };
  const rgb = named[String(value || '').trim().toLowerCase()] || parseHexColor(value);
  if (!rgb) return null;
  return (0.299 * rgb[0] + 0.587 * rgb[1] + 0.114 * rgb[2]) / 255;
}

const LIGHT_THRESHOLD = 0.85;
const DARK_THRESHOLD = 0.35;

export default {
  components: {
    BackIcon,
    BackDarkIcon
  },
  props: {
    fixed: {
      type: Boolean,
      default: false,
    },
    sticky: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    smallTitle: {
      type: String,
      default: '',
    },
    background: {
      type: String,
      default: 'none',
    },
    color: {
      type: String,
      default: '#070707',
    },
    borderColor: {
      type: String,
      default: 'none',
    },
    hideBack: {
      type: Boolean,
      default: false,
    },
    backColor: {
      type: String,
      default: 'black',
    },
    isNewVersion: {
      type: Boolean,
      default: false,
    },
    customBackFn: Function,
    backComponent: {
      type: [Object, Function],
      default: null,
    },
  },
  data() {
    return {
      paddingTop: '0.44rem',
      whiteBackIcon: require('@/assets/image/icons/arrow-left-white.png'),
      blackBackIcon: require('@/assets/image/icons/arrow-left-black.png'),
    }
  },
  computed: {
    safePaddingTop() {
      // Do not infer the safe area from an iPhone model allowlist. Dynamic
      // Island and Android cutout devices may expose safe-area-inset-top.
      // Keep the native statusBarHeight/default value as a fallback for WebViews
      // that do not expose safe-area-inset-top.
      const css = window.CSS
      if (css && css.supports) {
        if (css.supports('padding-top', 'max(env(safe-area-inset-top, 0px), 1px)')) {
          return `max(env(safe-area-inset-top, 0px), ${this.paddingTop})`
        }
        // iOS 11.0/11.1 used constant() before safe-area-inset-* moved to env().
        if (css.supports('padding-top', 'constant(safe-area-inset-top)')) {
          if (css.supports('padding-top', 'max(constant(safe-area-inset-top), 1px)')) {
            return `max(constant(safe-area-inset-top), ${this.paddingTop})`
          }
          // Without CSS max(), constant() is only useful on legacy notched
          // iPhones. On iPhone 8/SE it resolves to zero and would remove the
          // header's original status-bar spacing.
          return this.$util.isiPhoneX
            ? 'constant(safe-area-inset-top)'
            : this.paddingTop
        }
      }
      return this.paddingTop
    },
    pageDark() {
      return isPageDark()
    },
    themedBackground() {
      const lum = luminance(this.background)
      if (this.pageDark && lum !== null && lum >= LIGHT_THRESHOLD) {
        return 'var(--nc-bg-page)'
      }
      return this.background
    },
    themedColor() {
      const lum = luminance(this.color)
      if (this.pageDark && lum !== null && lum <= DARK_THRESHOLD) {
        return 'var(--nc-text-primary)'
      }
      return this.color
    },
    themedBorderColor() {
      const lum = luminance(this.borderColor)
      if (this.pageDark && lum !== null && lum >= LIGHT_THRESHOLD) {
        return 'var(--nc-line)'
      }
      return this.borderColor
    },
    // 深色頁面裡黑色返回鍵改用白色
    effectiveBackColor() {
      if (this.pageDark && this.backColor === 'black') return 'white'
      return this.backColor
    },
    backSrc() {
      if (this.effectiveBackColor === 'white') {
        return this.whiteBackIcon
      } else if (this.effectiveBackColor === 'black') {
        return this.blackBackIcon
      } else {
        return this.effectiveBackColor
      }
    },
    backIconComponent() {
      return this.effectiveBackColor === 'white' ? BackIcon : BackDarkIcon
    }
  },
  mounted() {
    if (this.$util.onVersion('1.22.0')) {
      // 設置statusBar高度
      const statusBarHeight = this.$route.query.statusBarHeight
      const normalizedStatusBarHeight = String(statusBarHeight == null ? '' : statusBarHeight).trim()
      const isValidStatusBarHeight = /^(?:\d+\.?\d*|\.\d+)$/.test(normalizedStatusBarHeight)
      const parsedStatusBarHeight = Number(normalizedStatusBarHeight)
      if (isValidStatusBarHeight && Number.isFinite(parsedStatusBarHeight) && parsedStatusBarHeight <= 200) {
        this.paddingTop = parsedStatusBarHeight + 'px'
      }
    }
    this.$nextTick(() => {
      // 传出header高度（物理像素）
      const header = this.$refs.header
      if (header) {
        const headerHeight = header.getBoundingClientRect().height
        this.$emit('mounted', headerHeight)
      }
    })
  },
  methods: {
    routeBack() {
      if (this.hideBack) return
      if (this.customBackFn) {
        this.customBackFn()
      } else {
        this.$util.goBack()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .header-wrap {
    position: relative;
    z-index: 3;
    flex-shrink: 0;
    width: 100%;
    top: 0;
    &.sticky {
      position: -webkit-sticky;
      position: sticky;
    }
  }
  .header-hold {
    position: relative;
    z-index: 0;
    height: 100px;
  }
  .header-self {
    box-sizing: content-box;
    position: relative;
    z-index: 1;
    top: 0;
    width: 100%;
    height: 100px;
    line-height: 36px;
    font-size: 36px;
    font-weight: 600;
    z-index: 99;
    color: #333;
    &.fixed {
      position: fixed;
      left: 0;
      top: 0;
    }
    .content {
      @include flex(space-between, center);
      position: relative;
      z-index: 1;
      width: 100%;
      height: 100px;
      padding: 0 36px;
      padding-left: unquote("max(36px, calc(constant(safe-area-inset-left) + 24px))");
      padding-left: unquote("max(36px, calc(env(safe-area-inset-left, 0px) + 24px))");
      padding-right: unquote("max(36px, calc(constant(safe-area-inset-right) + 24px))");
      padding-right: unquote("max(36px, calc(env(safe-area-inset-right, 0px) + 24px))");
      .btn-back {
        flex-shrink: 0;
        @include flex(center, center);
        position: relative;
        height: 56px;
        font-size: 0;
        &::before {
          content: '';
          position: absolute;
          top: -16px;
          right: -16px;
          bottom: -16px;
          left: -16px;
        }
        img {
          width: 56px;
          height: 56px;
          &.hidden {
            visibility: hidden;
          }
        }
      }
      .title {
        flex-grow: 1;
        @include flex;
        overflow: hidden;
        align-self: stretch;
      }
      .tools {
        flex-shrink: 0;
        justify-self: flex-end;
        position: relative;
        z-index: 1;
        min-width: 56px;
      }
    }
  }
.content {
  &.new {
    .title {
      display: flex;
      flex-direction: column;
      color: var(--nc-text-primary, #111111);
      font-size: 30px;
      .small-title {
        font-size: 24px;
        color: var(--nc-text-secondary, rgba(128, 128, 128, 0.77));
        font-weight: 400;
      }
    }
    .icon-box {
      width: 56px;
      height: 56px;
    }
  }
}
</style>
